import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { LoadingScreen } from './components/LoadingScreen';
import { FloatingBubbles } from './components/FloatingBubbles';
import { HelmetProvider } from 'react-helmet-async';
import { CookieConsent } from './components/CookieConsent';
import { useState, useEffect, lazy, Suspense } from 'react';
import { ScrollToTop } from './components/ScrollToTop';
import React from 'react';

// Lazy load page components
const Home = lazy(() => import('./pages/Home').then(module => ({ default: module.Home })));
const History = lazy(() => import('./pages/History').then(module => ({ default: module.History })));
// const Gallery = lazy(() => import('./pages/Gallery')); // Gallery is commented out and likely uses a named export too if uncommented.
const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));
const Contact = lazy(() => import('./pages/Contact').then(module => ({ default: module.Contact })));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy')); // Uses default export
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy')); // Uses default export
const Terms = lazy(() => import('./pages/Terms')); // Uses default export

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 text-stone-800 p-8">
          <h1 className="text-3xl font-bold mb-4">Something went wrong</h1>
          <p className="mb-6">An unexpected error occurred. Please try reloading the page.</p>
          <button className="btn-primary" onClick={() => window.location.reload()}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
    document.documentElement.className = i18n.language === 'ar' ? 'font-arabic' : '';
    setLoading(false);
  }, [i18n.language]);

  if (loading) {
    // This initial loading screen is now redundant with lazy loading on routes
    // return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="min-h-screen bg-stone-50 text-stone-800 font-sans">
          <FloatingBubbles />
          <Navbar />
          <Suspense fallback={<LoadingScreen />}>
            <main className="transition-all duration-300 ease-in-out">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/history" element={<History />} />
                {/* <Route path="/gallery" element={<Gallery />} /> */}
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="*" element={<Home />} />
              </Routes>
            </main>
          </Suspense>
          <Footer />
          <CookieConsent />
        </div>
      </BrowserRouter>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;